import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditTrailPanel = ({ auditLogs = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');

  const actionOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'view', label: 'Viewed' },
    { value: 'edit', label: 'Edited' },
    { value: 'create', label: 'Created' },
    { value: 'delete', label: 'Deleted' }
  ];

  const getActionIcon = (action) => {
    switch (action) {
      case 'view':
        return 'Eye';
      case 'edit':
        return 'Edit';
      case 'create':
        return 'Plus';
      case 'delete':
        return 'Trash2';
      default:
        return 'Activity';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'view':
        return 'text-primary';
      case 'edit':
        return 'text-warning';
      case 'create':
        return 'text-success';
      case 'delete':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const filteredLogs = auditLogs?.filter(log => {
    const matchesSearch = log?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.user?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter = filterAction === 'all' || log?.action === filterAction;
    return matchesSearch && matchesFilter;
  });

  if (auditLogs?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
          <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
        </div>
        <p className="text-base font-medium text-foreground mb-2">No Audit Logs</p>
        <p className="text-sm text-muted-foreground">Record modification history will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search audit logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            options={actionOptions}
            value={filterAction}
            onChange={setFilterAction}
            placeholder="Filter by action"
          />
        </div>
      </div>
      <div className="space-y-2">
        {filteredLogs?.map((log) => (
          <div
            key={log?.id}
            className="bg-card border border-border rounded-lg p-4 transition-smooth hover:shadow-elevation-2"
          >
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 ${getActionColor(log?.action)}`}>
                <Icon name={getActionIcon(log?.action)} size={20} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                  <p className="text-sm md:text-base font-medium text-foreground">
                    {log?.description}
                  </p>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {log?.timestamp}
                  </span>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Icon name="User" size={14} />
                    <span>{log?.user}</span>
                  </div>
                  {log?.role && (
                    <div className="flex items-center gap-1.5">
                      <Icon name="Shield" size={14} />
                      <span>{log?.role}</span>
                    </div>
                  )}
                  {log?.ipAddress && (
                    <div className="flex items-center gap-1.5">
                      <Icon name="Globe" size={14} />
                      <span>{log?.ipAddress}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredLogs?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
          <p className="text-sm text-muted-foreground mt-2">No matching audit logs found</p>
        </div>
      )}
    </div>
  );
};

export default AuditTrailPanel;